<ng-container *ngIf="vm$ | async as vm">
  <nz-table
    class="mb-64 basis-[calc(50%-8px)] flex-grow-default"
    id="systemDetailsTable"
    #systemDetailsTable
    nzShowPagination
    nzShowSizeChanger
    [nzFrontPagination]="true"
    [nzHideOnSinglePage]="true"
    nzPaginationType="small"
    nzSimple="true"
    [nzPageSize]="10"
    [nzData]="vm.rows"
    [nzBordered]="false"
  >
    <thead>
      <tr class="custom-header">
        <th
          class="text-xs items-center py-0"
          *ngFor="let column of vm.columns; trackBy: trackColumn"
          [nzSortOrder]="column.sortOrder"
          [nzSortFn]="column.sortFn"
          [nzSortDirections]="column.sortDirections"
          [nzFilters]="column.filters"
          [nzFilterFn]="column.filterFn"
          [nzFilterMultiple]="true"
          [nzWidth]="column.width"
        >
          <div class="flex items-center gap-4 text-xs font-semibold leading-lg" [ngClass]="column.class">
            {{ column.name }}

            <!-- The header cell toggles the sort; the info icon must not. -->
            <twaice-fe-popover-icon
              *ngIf="column.tooltip"
              (click)="$event.stopPropagation()"
              [popoverContent]="columnPopoverContent"
              nzPopoverPlacement="top"
              customClasses="w-24 h-24"
            ></twaice-fe-popover-icon>

            <!-- popover start -->
            <ng-template #columnPopoverContent>
              <div class="max-w-[370px]">
                <h2 class="text-lg text-start font-semibold">{{ column.tooltip.title ?? column.name }}</h2>

                <div class="mt-4 text-gray-600 text-justify">
                  <p>{{ column.tooltip.content }}</p>
                </div>

                <div class="mt-6" *ngIf="column.tooltip.linkText">
                  <a target="_blank" rel="noopener noreferrer" [href]="column.tooltip.link" class="text-blue-600 text-sm">{{
                    column.tooltip.linkText
                  }}</a>
                </div>
              </div>
            </ng-template>

            <!-- popover end -->
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let data of systemDetailsTable.data" [attr.data-testid]="'system-overview-row-' + data.systemBk">
        <td class="align-top p-24">
          <div class="flex flex-col gap-8">
            <div class="text-xs font-semibold leading-[20px] flex flex-row justify-between items-center">
              {{ data?.systemName }}

              <div class="flex items-center gap-8">
                <button
                  (click)="navigate(data, 'storage-hierarchy')"
                  nz-popover
                  nzPopoverContent="Hierarchy Diagram"
                  nzPopoverPlacement="top"
                  class="flex items-center justify-center"
                >
                  <twaice-fe-svg-icon source="tabler" name="tournament" width="14px" height="14px" />
                </button>
                <button
                  (click)="openChat(data.customerBk, data.systemBk, data.systemName)"
                  nz-popover
                  nzPopoverContent="System Notes"
                  nzPopoverPlacement="top"
                  class="flex items-center justify-center"
                >
                  <nz-badge [nzDot]="data.newChatMessagesAvailable">
                    <twaice-fe-svg-icon source="tabler" name="messages" width="16px" height="16px" />
                  </nz-badge>
                </button>
              </div>
            </div>
            <span class="details-data text-xs text-secondary-text leading-[20px]">{{ data.storageLocation }}</span>
            <div class="flex items-center gap-8 text-xs">
              <span class="text-tertiary-text text-xs leading-lg w-64">{{
                data?.bessMetadata?.nameplateEnergyCapacityMwh
                  ? (data?.bessMetadata?.nameplateEnergyCapacityMwh | number: '1.0-0') + ' MWh'
                  : '--'
              }}</span>
              <span class="border-l-1 h-16 w-1 p-4"></span>
              <span class="text-tertiary-text text-xs leading-lg w-64">{{
                data?.bessMetadata?.nameplatePowerMw ? (data?.bessMetadata?.nameplatePowerMw | number: '1.0-0') + ' MW' : '--'
              }}</span>
            </div>
          </div>
        </td>
        <td
          nz-tooltip
          [nzTooltipTitle]="data.performance?.isFeatureUnavailable ? null : performanceTooltip"
          (click)="navigate(data, data.performance?.link)"
          class="relative align-top p-24"
          [ngClass]="{ 'hover:shadow-default transition-shadow cursor-pointer': !data.performance?.isFeatureUnavailable }"
        >
          <div class="flex flex-col">
            <div class="flex flex-col items-center gap-8">
              <twaice-fe-svg-icon
                *ngIf="data.performance?.icon"
                [name]="data.performance?.icon"
                source="tabler"
                width="12px"
                height="12px"
                [color]="data.performance?.color"
              />
              <div class="text-secondary-text text-center text-xs leading-[20px] w-[150px]">
                <ng-container *ngIf="data.performance?.label">
                  <span *ngIf="data.performance?.label"> {{ data.performance?.label | levelIdentifierToLevelName }}</span>
                </ng-container>

                <ng-container *ngIf="!data.performance?.icon">
                  <nz-badge class="-mr-8 mb-8" [nzColor]="data.performance?.color"></nz-badge>

                  <p class="mb-0">
                    {{ data.usableEnergyPercentage ? (data.usableEnergyPercentage | number: '1.0-1') : 'N/A' }} % of energy is
                    usable
                  </p>
                </ng-container>
              </div>
            </div>
          </div>
          <ng-template #performanceTooltip>
            <span
              >{{ data.recoverableEnergyMWh ? (data.recoverableEnergyMWh | number: '1.0-2') : 'N/A' }} MWh of energy are
              recoverable.
            </span>
            <span *ngIf="!vm.isFinancialImpactDisabled && data.pricePerMwh && data.recoverableEnergyMWh"
              >This has a financial impact of
              {{ data.pricePerMwh.value * data.recoverableEnergyMWh | currency: data.pricePerMwh.currency : 'symbol' : '1.0-0' }}
              per cycle.</span
            >
          </ng-template>
        </td>
        <td
          (click)="navigate(data, data.safetyScore?.link)"
          class="relative align-top p-24"
          [ngClass]="{ 'hover:shadow-default transition-shadow cursor-pointer': !data.safetyScore?.isFeatureUnavailable }"
        >
          <div class="flex flex-col">
            <div class="flex flex-col items-center gap-8">
              <twaice-fe-svg-icon
                *ngIf="data.safetyScore?.icon"
                [name]="data.safetyScore?.icon"
                source="tabler"
                width="12px"
                height="12px"
                [color]="data.safetyScore?.color"
              />
              <nz-badge class="-mr-8" *ngIf="!data.safetyScore?.icon" [nzColor]="data.safetyScore?.color"></nz-badge>
              <span class="text-secondary-text text-center text-xs leading-[20px] w-[150px]">
                <span *ngIf="data.safetyScore?.value">{{ data.safetyScore?.value }} %</span>
                {{ data.safetyScore?.label }}</span
              >
            </div>
          </div>
        </td>
        <td
          *ngIf="vm.isPenaltyRiskEnabled"
          (click)="navigate(data, data.risk?.link)"
          class="relative align-top p-24"
          [ngClass]="{ 'hover:shadow-default transition-shadow cursor-pointer': !data.risk?.isFeatureUnavailable }"
        >
          <div class="flex flex-col">
            <div class="flex flex-col items-center gap-8">
              <twaice-fe-svg-icon
                *ngIf="data.risk?.icon"
                [name]="data.risk?.icon"
                source="tabler"
                width="12px"
                height="12px"
                [color]="data.risk?.color"
              />
              <nz-badge class="-mr-8" *ngIf="!data.risk?.icon" [nzColor]="data.risk?.color"></nz-badge>
              <span class="text-secondary-text text-center text-xs leading-[20px] w-[150px]">
                <span *ngIf="data.risk?.value">{{ data.risk?.value }} %</span>
                {{ data.risk?.label }}</span
              >
            </div>
          </div>
        </td>
        <td
          (click)="navigate(data, data.warrantyState?.link)"
          class="relative hover:shadow-default transition-shadow cursor-pointer align-top p-24"
        >
          <div class="flex flex-col">
            <div class="flex flex-col items-center gap-8">
              <twaice-fe-svg-icon
                *ngIf="data.warrantyState?.icon"
                [name]="data.warrantyState?.icon"
                source="tabler"
                width="12px"
                height="12px"
                [color]="data.warrantyState?.color"
              />
              <nz-badge class="-mr-8" *ngIf="!data.warrantyState?.icon" [nzColor]="data.warrantyState?.color"></nz-badge>
              <span class="text-secondary-text text-center text-xs leading-[20px] w-[150px]">
                {{ data.warrantyState?.label }}</span
              >
            </div>
          </div>
        </td>
        <td
          *ngIf="vm.isRTESummaryEnabled"
          (click)="navigate(data, data.rte?.link)"
          class="relative hover:shadow-default transition-shadow cursor-pointer align-top p-24"
        >
          <div class="flex flex-col">
            <div class="flex flex-col items-center gap-8">
              <twaice-fe-svg-icon
                *ngIf="data.rte?.icon"
                [name]="data.rte?.icon"
                source="tabler"
                width="12px"
                height="12px"
                [color]="data.rte?.color"
              />
              <nz-badge class="-mr-8" *ngIf="!data.rte?.icon" [nzColor]="data.rte?.color"></nz-badge>
              <span class="text-secondary-text text-center text-xs leading-[20px] w-[150px]">
                <span *ngIf="data.rte?.value">{{ data.rte?.value }} %</span>
                {{ data.rte?.label }}</span
              >
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  </nz-table>
</ng-container>
